import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import PartnersPage from "@/pages/PartnersPage";
import TransactionPage from "@/pages/TransactionPage";
import POS from "@/pages/POS";
import Sales from "@/pages/Sales";
import Customers from "@/pages/Customers";
import Expenses from "@/pages/Expenses";
import CashFlow from "@/pages/CashFlow";
import Modal from "@/pages/Modal";
import BalanceSheet from "@/pages/BalanceSheet";
import Reports from "@/pages/Reports";
import StoreSales from "@/pages/StoreSales";
import StoreDetail from "@/pages/StoreDetail";
import SuperAdmin from "@/pages/SuperAdmin";
import MitraPortal from "@/pages/MitraPortal";
import MitraHistory from "@/pages/MitraHistory";
import MitraIdentitas from "@/pages/MitraIdentitas";
import AdminMitraPortal from "@/pages/AdminMitraPortal";
import StockAdjustmentReview from "@/pages/StockAdjustmentReview";
import Announcements from "@/pages/Announcements";
import Investor from "@/pages/Investor";
import StoreSalesHistory from "@/pages/StoreSalesHistory";
import MitraOrders from "@/pages/MitraOrders";
import AdminOrders from "@/pages/AdminOrders";

function Guard({ children, allow }) {
  const { user, loading, role } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && role !== "owner" && !allow.includes(role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

// Root redirect based on role
function RoleHome() {
  const { role } = useAuth();
  if (role === "mitra") return <Navigate to="/mitra" replace />;
  if (role === "investor") return <Navigate to="/investor" replace />;
  return <Dashboard />;
}

const FIN = ["finance"];
const OPS = ["manager_ops"];
const POSR = ["kasir", "staff", "manager_ops"];
const MITRA_VIEWERS = ["finance", "manager_ops", "investor"];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Guard><RoleHome /></Guard>} />
      <Route path="/pos" element={<Guard allow={POSR}><POS /></Guard>} />
      <Route path="/products" element={<Guard allow={[...OPS, ...FIN]}><Products /></Guard>} />
      <Route path="/sales" element={<Guard allow={[...POSR, ...FIN]}><Sales /></Guard>} />
      <Route path="/customers" element={<Guard allow={POSR}><Customers /></Guard>} />
      <Route path="/purchases" element={<Guard allow={OPS}><TransactionPage mode="purchase" /></Guard>} />
      <Route path="/distributions" element={<Guard allow={OPS}><TransactionPage mode="distribution" /></Guard>} />
      <Route path="/store-sales" element={<Guard allow={[...OPS, ...FIN]}><StoreSales /></Guard>} />
      <Route path="/store-sales-history" element={<Guard allow={[...OPS, ...FIN]}><StoreSalesHistory /></Guard>} />
      <Route path="/admin/orders" element={<Guard allow={OPS}><AdminOrders /></Guard>} />
      <Route path="/mitra/orders" element={<Guard allow={["mitra"]}><MitraOrders /></Guard>} />
      <Route path="/stores/:id" element={<Guard allow={OPS}><StoreDetail /></Guard>} />
      <Route path="/expenses" element={<Guard allow={[...FIN, ...OPS]}><Expenses /></Guard>} />
      <Route path="/cashflow" element={<Guard allow={FIN}><CashFlow /></Guard>} />
      <Route path="/modal" element={<Guard allow={FIN}><Modal /></Guard>} />
      <Route path="/balance-sheet" element={<Guard allow={FIN}><BalanceSheet /></Guard>} />
      <Route path="/reports" element={<Guard allow={FIN}><Reports /></Guard>} />
      <Route path="/distributors" element={<Guard allow={OPS}><PartnersPage kind="distributors" /></Guard>} />
      <Route path="/stores" element={<Guard allow={OPS}><PartnersPage kind="stores" /></Guard>} />
      <Route path="/super-admin" element={<Guard allow={[]}><SuperAdmin /></Guard>} />
      <Route path="/mitra" element={<Guard allow={["mitra"]}><MitraPortal /></Guard>} />
      <Route path="/mitra/history" element={<Guard allow={["mitra"]}><MitraHistory /></Guard>} />
      <Route path="/mitra/identitas" element={<Guard allow={["mitra"]}><MitraIdentitas /></Guard>} />
      <Route path="/admin/mitra" element={<Guard allow={MITRA_VIEWERS}><AdminMitraPortal /></Guard>} />
      <Route path="/admin/opname-review" element={<Guard allow={[]}><StockAdjustmentReview /></Guard>} />
      <Route path="/announcements" element={<Guard allow={["mitra", "manager_ops", "finance", "kasir", "investor", "staff"]}><Announcements /></Guard>} />
      <Route path="/investor" element={<Guard allow={["investor"]}><Investor /></Guard>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
